import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { sendAdminNotification } from "../../../api/services/notificationService";

interface NotificationFormProps {
  toggleDrawer: (open: boolean) => void;
}

export const NotificationForm = ({ toggleDrawer }: NotificationFormProps) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [messageAr, setMessageAr] = useState("");
  const [recipientType, setRecipientType] = useState("ALL_USERS");
  const [sendEmail, setSendEmail] = useState(false);
  const [sendPush, setSendPush] = useState(true);
  const [sendSms, setSendSms] = useState(false);

  const { mutate: createNotificationMutate, isLoading } = useMutation(
    sendAdminNotification,
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("notifications-list");
        toast.success(res?.message || "Notification sent successfully");
        toggleDrawer(false);
        resetForm();
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to send notification"
        );
      },
    }
  );

  const resetForm = () => {
    setTitleAr("");
    setMessageAr("");
    setTitle("");
    setMessage("");
    setRecipientType("ALL_USERS");
    setSendEmail(false);
    setSendPush(true);
    setSendSms(false);
  };

  const onSubmit = () => {
    // Determine notification type based on selected channels
    let notificationType: "EMAIL" | "PUSH" | "BOTH" = "PUSH";
    if (sendEmail && sendPush) {
      notificationType = "BOTH";
    } else if (sendEmail) {
      notificationType = "EMAIL";
    }

    const payload = {
      notificationType,
      recipientType: recipientType as any,
      title,
      message,
      titleAr: titleAr || undefined,
      messageAr: messageAr || undefined,
    };

    createNotificationMutate(payload);
  };

  return (
    <Box width="450px">
      <Typography variant="h6" sx={{ mb: 2 }}>
        English Version
      </Typography>

      <TextField
        fullWidth
        label="Notification Title (English)"
        variant="standard"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        label="Message (English)"
        variant="standard"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        multiline
        rows={4}
        required
      />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        Arabic Version (عربي)
      </Typography>

      <TextField
        fullWidth
        label="عنوان الإشعار (Arabic Title)"
        variant="standard"
        value={titleAr}
        onChange={(e) => setTitleAr(e.target.value)}
        inputProps={{ dir: "rtl" }}
        helperText="Optional - Will use English if not provided"
      />

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        label="الرسالة (Arabic Message)"
        variant="standard"
        value={messageAr}
        onChange={(e) => setMessageAr(e.target.value)}
        multiline
        rows={4}
        inputProps={{ dir: "rtl" }}
        helperText="Optional - Will use English if not provided"
      />

      <TextField
        sx={{ mt: 3 }}
        fullWidth
        select
        label="Recipient Type"
        variant="standard"
        value={recipientType}
        onChange={(e) => setRecipientType(e.target.value)}
        required
      >
        <MenuItem value="ALL_USERS">All Users</MenuItem>
        <MenuItem value="ALL_CLINICIANS">All Clinicians</MenuItem>
      </TextField>

      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle2" mb={1}>
          Notification Channels
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={sendPush}
              onChange={(e) => setSendPush(e.target.checked)}
            />
          }
          label="Push Notification"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
          }
          label="Email"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sendSms}
              onChange={(e) => setSendSms(e.target.checked)}
            />
          }
          label="SMS"
        />
      </Box>

      <Button
        type="submit"
        onClick={onSubmit}
        variant="contained"
        fullWidth
        disabled={isLoading || !title || !message}
      >
        {isLoading ? "Sending..." : "Send Notification"}
      </Button>
    </Box>
  );
};
